import React from 'react';
import debounce from 'lodash/debounce';
import differenceBy from 'lodash/differenceBy';
import isPlainObject from 'lodash/isPlainObject';
import isArray from 'lodash/isArray';
import pick from 'lodash/pick';
import unionBy from 'lodash/unionBy';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import orderBy from 'lodash/orderBy';

import Tasks from '@catalytic/pushbot-ui/SvgIcon/Tasks';
import NavTemplate from '@catalytic/pushbot-ui/SvgIcon/Nav/NavTemplates';
import ProcessOutline from '@catalytic/pushbot-ui/SvgIcon/ProcessOutline';

import errorLog from './errorLog';
import { appNameToIcon } from './integrations';
import IMPLICIT_RUN_FIELDS from '../const/implicitRunFields';

import { GET_VISIBLE_FIELDS_DEBOUNCE_AMOUNT } from '../actions/api/fields';
const IMPLICIT_RUN_FIELD_NAMES = IMPLICIT_RUN_FIELDS.map(f => f.fieldName);

const FILE_TYPES = ['file', 'photo'];

export const fieldIsFile = field => FILE_TYPES.includes(field.type);

export const filterAndSortByWritableFields = (fields, writableFields) => {
  if (!fields || !writableFields) return [];
  return uniq(writableFields)
    .map(fieldName =>
      fields.find(field => field && field.fieldName === fieldName)
    )
    .filter(f => f);
};

// Currently has no opinion on order of fields not included in writableFields
export const sortByWritableFields = (fields, writableFields) => {
  if (Array.isArray(writableFields)) {
    const cleanWritableFields = uniq(writableFields).filter(f => f);
    return orderBy(fields, field =>
      cleanWritableFields.indexOf(field.fieldName)
    );
  } else {
    return fields;
  }
};

export const fieldValueIsUndefined = field => {
  return field.value == null || field.value === ''; // false and 0 are not (none)
};

export const getFieldValue = field => {
  let value = field.value;

  if (fieldValueIsUndefined(field)) {
    value = '(none)';
  } else if (
    Array.isArray(field.value) &&
    field.value.every(i => typeof i === 'string')
  ) {
    value = field.value.join(', ');
  } else if (isPlainObject(field.value)) {
    value = JSON.stringify(field.value);
  }

  return '' + value;
};

export const getFileIDFromField = field => {
  if (isArray(field.value) && field.value.length) {
    // report old-style array file fields as a warning so we know to clean up
    if (field.value.length > 1) {
      errorLog.warning(
        'Encountered field with value length > 1',
        pick(field, ['teamName', 'fieldID'])
      );
    }
    if (typeof field.value[0] === 'object') {
      return field.value[0].fileID;
    }
    return field.value[0];
  }

  return getFieldValue(field);
};

function findPreviousFieldVersion(field, fields) {
  return find(fields, _field => {
    return _field.fieldID === field.previousVersion;
  });
}

function findNextFieldVersion(field, fields) {
  return find(fields, _field => {
    return _field.previousVersion === field.fieldID;
  });
}

export const getMostRecentAvailableFieldVersion = (field, fields) => {
  const nextField = findNextFieldVersion(field, fields);
  if (nextField && nextField.fieldID !== field.fieldID) {
    return getMostRecentAvailableFieldVersion(nextField, fields);
  } else {
    return field;
  }
};

export const filterFieldsToLatestFieldVersion = fields => {
  return uniq(
    fields.map(field => getMostRecentAvailableFieldVersion(field, fields))
  );
};

export const getAllFieldVersionsForField = (field, fields) => {
  const mostRecentVersion = getMostRecentAvailableFieldVersion(field, fields);
  return _getAllFieldVersionsForFieldRecurse(mostRecentVersion, fields);
};

function _getAllFieldVersionsForFieldRecurse(field, fields) {
  const previousField = findPreviousFieldVersion(field, fields);
  if (previousField && previousField.fieldID !== field.fieldID) {
    return [field].concat(
      _getAllFieldVersionsForFieldRecurse(previousField, fields)
    );
  } else {
    return [field];
  }
}

export const findFieldByDisplayName = function(field, fields) {
  const { displayName } = field || {};
  if (displayName) {
    return fields.find(
      ({ displayName: candidateDisplayName }) =>
        candidateDisplayName &&
        candidateDisplayName.toUpperCase() === displayName.toUpperCase()
    );
  }
};

export const collapseFieldsByFieldName = fields => {
  return Object.values(
    (fields || []).reduce((o, nextField) => {
      const key = nextField.fieldName;
      const lastField = o[key];
      o[key] =
        lastField !== undefined
          ? orderBy(
              [lastField, nextField],
              ['runID', 'createdDate'],
              [true, true]
            )[0]
          : nextField;
      return o;
    }, {})
  );
};

export const createConditionalFieldUpdater = function(
  getIsConditionalFieldsEnabled,
  getIsLoading,
  toggleLoading,
  getIsValidFieldLoadState,
  setFields,
  handleError,
  getLatestFieldState
) {
  return function(_updatedFields) {
    const {
      conditionalFieldsEnabled,
      fields: masterFieldList,
      getVisibleFields,
      writableFields,
      processID,
      runID,
      teamName
    } = this.props;

    const updatedFields = _updatedFields || masterFieldList;

    if (!conditionalFieldsEnabled || !getIsValidFieldLoadState()) {
      if (getIsLoading()) {
        toggleLoading(false);
      }

      setFields(sortByWritableFields(updatedFields, writableFields));
      return Promise.resolve();
    }

    let fields = unionBy(updatedFields, masterFieldList, 'fieldName');

    // TODO: Remove trust in the client's `field.conditionCode` (this saves API calls though)
    if (!getIsConditionalFieldsEnabled()) {
      if (getIsLoading()) {
        toggleLoading(false);
      }
      setFields(filterAndSortByWritableFields(fields, writableFields));
      return Promise.resolve();
    }

    // TODO: Don't tie to `StartPushbot` implementation
    const owner = this.state && this.state.runOwner && this.state.runOwner[0];

    return getVisibleFields({
      fields,
      owner,
      processID,
      runID,
      teamName
    })
      .then(() => {
        const { hiddenFields } = this.props;

        if (getLatestFieldState) {
          fields = unionBy(getLatestFieldState(), fields, 'fieldName');
        }

        const visibleFields = filterAndSortByWritableFields(
          differenceBy(fields, hiddenFields, 'fieldName'),
          writableFields
        );

        setFields(visibleFields);
      })
      .catch(handleError)
      .then(() => toggleLoading(false));
  };
};

export const createConditionalFieldUpdateHandler = function(
  updateFieldsFunction,
  isConditionalFieldsEnabled
) {
  return isConditionalFieldsEnabled
    ? debounce(updateFieldsFunction, GET_VISIBLE_FIELDS_DEBOUNCE_AMOUNT, {
        trailing: true
      }).bind(this) // Ensure this is bound correctly in consumer component!
    : updateFieldsFunction;
};

export const createConditionalFieldChangeHandler = function(
  getIsConditionalFieldsEnabled,
  handleFieldsUpdate,
  getIsLoading,
  toggleLoading
) {
  return function(fields) {
    handleFieldsUpdate(fields);

    if (getIsConditionalFieldsEnabled() && !getIsLoading()) {
      toggleLoading(true);
    }
  };
};

// https://github.com/catalyticlabs/pushbot-api/blob/master/lib/util/handlebars-helper.js#L6
const HANDLEBARS_REGEX = /{{([^(){}!]+?)}}/g;

export const getReferencedPaths = template =>
  ((template || '').match(HANDLEBARS_REGEX) || [])
    .map(match => match.slice(2, -2))
    .map(match => match.trim());

export const getFieldOriginStep = (field, process) => {
  if (!process || !field) {
    return null;
  }

  const steps = process.steps || [];
  const originStep = steps
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .find((step = {}) => {
      const stepWritableFields = step.writableFields || [];
      return stepWritableFields.includes(field.fieldName);
    });

  return originStep || null;
};

export const getFieldOriginIcon = (field, process) => {
  if (!field) {
    return null;
  }

  if (IMPLICIT_RUN_FIELD_NAMES.includes(field.fieldName)) {
    return <ProcessOutline />;
  }

  if (field.appName) {
    const Icon = appNameToIcon(field.appName, field.appID);
    return <Icon />;
  }

  if (!process) {
    return null;
  }

  const originStep = getFieldOriginStep(field, process);
  if (originStep) {
    const appStep = originStep.appStep;
    if (appStep && appStep.appName) {
      const Icon = appNameToIcon(appStep.appName, appStep.appID);
      return <Icon />;
    } else {
      // the default viewBox makes the icon visibly smaller than other icons
      return <Tasks viewBox="8 8 28 28" />;
    }
  }

  return <NavTemplate />;
};

getFieldOriginIcon.displayName = 'Field Origin Icon';



// WEBPACK FOOTER //
// ./js/util/fields.js